import React, { useState, useEffect, useRef } from "react";
import "./style.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  useGetContactContentDataQuery,
  useSubmitContactFormMutation,
} from "../../../services/contactAPI";
import { Link } from "react-router-dom";
import { useGetColorThemeQuery } from "../../../services/commonAPI";
import Loader from "../fullPageLoader"
import ReCAPTCHA from "react-google-recaptcha";

const siteKey = process.env.REACT_APP_RECAPTCHA_SITEKEY;

function Contactform() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);

  const recaptchaRef = useRef(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };
  const [submitContactForm, { isLoading }] = useSubmitContactFormMutation();

  const {
    data: colorthemeData,
  } = useGetColorThemeQuery();

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000); // Message disappears after 5 seconds

      return () => clearTimeout(timer); // Cleanup on unmount
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Remove the error for the corresponding field
    setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));

    setFormData({ ...formData, [name]: value });
  };


  const validateForm = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    else if (formData.message.length < 10) newErrors.message = "Message must be at least 10 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!validateForm()) return;

    if (!captchaValue) {
      alert("Please verify the reCAPTCHA!");
      return;
    }


    let updatedFormData = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      recaptchatoken: captchaValue
    }

    try {
      const response = await submitContactForm(updatedFormData).unwrap();
      setMessage(response.message);
      setMessageType("success");

      // ✅ Reset form data state (if using state)
      setFormData({ name: "", email: "", subject: "", message: "" });
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaValue(null);

    } catch (error) {
      setMessage(error.data?.message || "Failed to submit the form.");
      setMessageType("error");
    }
  };


  const {
    data: contactContent,
    error: contentError,
    isLoading: contentLoading,
  } = useGetContactContentDataQuery();

  if (contentLoading) return <Loader />;
  if (contentError) return <p>Failed to load page content</p>;

  // Get the first section of the about content
  const contactSection = contactContent?.data?.[0];

  const handleCall = () => {
    window.location.href = `tel:${contactSection.phone}`;
  };

  const handleEmail = () => {
    window.location.href = `mailto:${contactSection.email}`;
  };

  const handleAddress = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactSection.address)}`, '_blank');
  };



  const handleTextKeyPress = (e) => {
    const regex = /^[a-zA-Z\s]$/;
    if (!regex.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleTextPaste = (field) => (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("Text");
    const cleanedText = pastedText.replace(/[^a-zA-Z\s]/g, "");
    setFormData((prev) => ({ ...prev, [field]: cleanedText }));
  };



  return (
    <div className="contact-form">
      <div className="container">
        <div className="row">
          <div className="col-md-6 col-sm-12 col-lg-6">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label>Your Name</label>
                <br />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onKeyPress={handleTextKeyPress}
                  onPaste={handleTextPaste("name")}
                  placeholder="Enter your Name"
                />
                {errors.name && <p className="error-text">{errors.name}</p>}
              </div>
              <div className="mb-4">
                <label>Your Email</label>
                <br />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your Email"
                />
                {errors.email && <p className="error-text">{errors.email}</p>}
              </div>
              <div className="mb-4">
                <label>Your Subject</label>
                <br />
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Enter your Subject"
                  onKeyPress={handleTextKeyPress}
                  onPaste={handleTextPaste("subject")}
                />
                {errors.subject && <p className="error-text">{errors.subject}</p>}
              </div>
              <div className="mb-4">
                <label>Your Message</label>
                <br />
                <textarea
                  rows={3}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Type your Message"
                  onKeyPress={handleTextKeyPress}
                  onPaste={handleTextPaste("message")}
                />
                {errors.message && <p className="error-text">{errors.message}</p>}
              </div>
              <div className="mb-4">
                <ReCAPTCHA
                  sitekey={siteKey}
                  onChange={handleCaptchaChange}
                  ref={recaptchaRef}
                />
              </div>

              <button
                type="submit"
                className="primary-button"
                disabled={isLoading}
                style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </form>

            {/* Display Success or Error Message */}
            {message && (
              <p
                className={
                  messageType === "success"
                    ? "success-message"
                    : "error-message"
                }
              >
                {message}
              </p>
            )}
          </div>
          <div className="col-md-6 col-sm-12 col-lg-6">
            <h4 style={{ color: colorthemeData?.color_themes?.[0]?.heading_color || "#000" }}>Get in touch</h4>
            <p style={{ color: colorthemeData?.color_themes?.[0]?.paragraph_color || "##767676" }}>{contactSection.content}</p>
            <div className="row">
              {contactSection.phone && <div className="col-md-6 col-sm-12 col-lg-6" onClick={handleCall}>
                <div className="d-flex gap-10">
                  <div>
                    <FontAwesomeIcon icon={faPhone} className="contact-icons" style={{ color: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }} />
                  </div>
                  <div>
                    <h5 style={{ color: colorthemeData?.color_themes?.[0]?.heading_color || "#000" }}>Call Us</h5>
                    <p style={{ color: colorthemeData?.color_themes?.[0]?.text_color_one || "#a5a5a5" }}>{contactSection.phone}</p>
                  </div>
                </div>
              </div>}

              {contactSection.email && <div className="col-md-6 col-sm-12 col-lg-6" onClick={handleEmail}>
                <div className="d-flex gap-10">
                  <div>
                    <FontAwesomeIcon icon={faEnvelope} className="contact-icons" style={{ color: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }} />
                  </div>
                  <div>
                    <h5 style={{ color: colorthemeData?.color_themes?.[0]?.heading_color || "#000" }}>Email Us</h5>
                    <p style={{ color: colorthemeData?.color_themes?.[0]?.text_color_one || "#a5a5a5" }}>{contactSection.email}</p>
                  </div>
                </div>
              </div>}


              {contactSection.address && <div className="col-md-6 col-sm-12 col-lg-6" onClick={handleAddress}>
                <div className="d-flex gap-10">
                  <div>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icons" style={{ color: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }} />
                  </div>
                  <div>
                    <h5 style={{ color: colorthemeData?.color_themes?.[0]?.heading_color || "#000" }}>Address</h5>
                    <p style={{ color: colorthemeData?.color_themes?.[0]?.text_color_one || "#a5a5a5" }}>{contactSection.address}</p>
                  </div>
                </div>
              </div>}

            </div>
            <h5 className="mt-2">Follow us</h5>
            <div className="social-media-links">
              {contactSection.facebook && (
                <span style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}>
                  <Link to={`https://${contactSection.facebook}`} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faFacebook} />
                  </Link>
                </span>
              )}

              {contactSection.twitter && (
                <span style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}>
                  <Link to={`https://${contactSection.twitter}`} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faTwitter} />
                  </Link>
                </span>
              )}

              {contactSection.instagram && (
                <span style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}>
                  <Link to={`https://${contactSection.instagram}`} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faInstagram} />
                  </Link>
                </span>
              )}

              {contactSection.youtube && (
                <span style={{ backgroundColor: colorthemeData?.color_themes?.[0]?.primary_color || "#a76d50" }}>
                  <Link to={`https://${contactSection.youtube}`} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faYoutube} />
                  </Link>
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Contactform;
